import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SiteSettings {
  social_facebook: string;
  social_instagram: string;
  social_x: string;
  social_youtube: string;
  social_email: string;
  footer_description: string;
  newsletter_title: string;
  newsletter_description: string;
  custom_css: string;
  site_body_text_color: string;
  site_heading_text_color: string;
  site_favicon: string;
  site_header_logo: string;
  site_footer_logo: string;
  site_admin_logo: string;
  newsletter_bg_image: string;
  featured_games_columns: string;
  games_page_columns: string;
  homepage_featured_games_title: string;
  homepage_business_motto_enabled: string;
  homepage_about_enabled: string;
  gtm_code: string;
  checkout_loading_title: string;
  checkout_loading_message: string;
  checkout_loading_logo: string;
  aiusage_hero_title: string;
  aiusage_hero_subtitle: string;
  aiusage_content: string;
  about_hero_label: string;
  about_hero_title: string;
  about_hero_text: string;
  about_mission_label: string;
  about_mission_title: string;
  about_mission_text: string;
  about_stat_1_value: string;
  about_stat_1_label: string;
  about_stat_2_value: string;
  about_stat_2_label: string;
  about_stat_3_value: string;
  about_stat_3_label: string;
  about_stat_4_value: string;
  about_stat_4_label: string;
  about_values_label: string;
  about_values_title: string;
  about_value_1_title: string;
  about_value_1_text: string;
  about_value_2_title: string;
  about_value_2_text: string;
  about_value_3_title: string;
  about_value_3_text: string;
  about_crew_label: string;
  about_crew_title: string;
  about_crew_text: string;
  about_cta_title: string;
  about_cta_text: string;
  about_cta_button_text: string;
  about_cta_button_url: string;
  contact_hero_label: string;
  contact_hero_title: string;
  contact_hero_text: string;
  contact_section_label: string;
  contact_section_title: string;
  contact_section_text: string;
  contact_reason_1_title: string;
  contact_reason_1_text: string;
  contact_reason_2_title: string;
  contact_reason_2_text: string;
  contact_reason_3_title: string;
  contact_reason_3_text: string;
  contact_cta_title: string;
  contact_cta_text: string;
  contact_cta_button_text: string;
  news_enabled: string;
  quote_enabled: string;
  topbar_enabled: string;
  topbar_text: string;
  returns_hero_title: string;
  returns_hero_subtitle: string;
  returns_content: string;
  privacy_hero_title: string;
  privacy_hero_subtitle: string;
  privacy_content: string;
  terms_hero_title: string;
  terms_hero_subtitle: string;
  terms_content: string;
}


interface SiteSettingsContextType {
  settings: SiteSettings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const defaultSettings: SiteSettings = {
  social_facebook: "",
  social_instagram: "",
  social_x: "",
  social_youtube: "",
  social_email: "",
  footer_description: "",
  newsletter_title: "",
  newsletter_description: "",
  custom_css: "",
  site_body_text_color: "#4d4d4d",
  site_heading_text_color: "#ffffff",
  site_favicon: "",
  site_header_logo: "",
  site_footer_logo: "",
  site_admin_logo: "",
  newsletter_bg_image: "",
  featured_games_columns: "4",
  games_page_columns: "3",
  homepage_featured_games_title: "Gather 'Round Ye Table",
  homepage_business_motto_enabled: "true",
  homepage_about_enabled: "true",
  gtm_code: "",
  checkout_loading_title: "Securing your order",
  checkout_loading_message:
    "We're handing your cart to TheGameCrafter, our printing partner. Checkout will open in a moment — please don't close this tab.",
  checkout_loading_logo: "",
  aiusage_hero_title: "",
  aiusage_hero_subtitle: "",
  aiusage_content: "",
  about_hero_label: "",
  about_hero_title: "",
  about_hero_text: "",
  about_mission_label: "",
  about_mission_title: "",
  about_mission_text: "",
  about_stat_1_value: "",
  about_stat_1_label: "",
  about_stat_2_value: "",
  about_stat_2_label: "",
  about_stat_3_value: "",
  about_stat_3_label: "",
  about_stat_4_value: "",
  about_stat_4_label: "",
  about_values_label: "",
  about_values_title: "",
  about_value_1_title: "",
  about_value_1_text: "",
  about_value_2_title: "",
  about_value_2_text: "",
  about_value_3_title: "",
  about_value_3_text: "",
  about_crew_label: "",
  about_crew_title: "",
  about_crew_text: "",
  about_cta_title: "",
  about_cta_text: "",
  about_cta_button_text: "",
  about_cta_button_url: "",
  contact_hero_label: "GET IN TOUCH",
  contact_hero_title: "Contact RSG",
  contact_hero_text:
    "Have a question about a rule? Want to say hi? Just want to chat about board games? Send us an email and we'll get back to you as soon as we can.",
  contact_section_label: "Send a Message",
  contact_section_title: "We're Here to Help",
  contact_section_text:
    "We are not a publisher that accepts unsolicited game submissions, but we welcome anything else. Pick the reason that fits best and click below to start an email.",
  contact_reason_1_title: "Say Ahoy",
  contact_reason_1_text:
    "Want to share some love, tell us about your game night, or just drop a friendly note? We'd love to hear it.",
  contact_reason_2_title: "Rule Questions",
  contact_reason_2_text:
    "Need clarity on a card, ability, or turn order? Send us your question and we'll get you back in the game.",
  contact_reason_3_title: "Ask Us Anything",
  contact_reason_3_text:
    "Curious about our process, upcoming releases, or where to find our games? No question is too small.",
  contact_cta_title: "Ready to reach out?",
  contact_cta_text: "Click the button below to open your email app and send us a message.",
  contact_cta_button_text: "Email Rogue Skull Games",
  news_enabled: "true",
  quote_enabled: "true",
  topbar_enabled: "true",
  topbar_text: "Site Under Construction: game info is being updated",
  returns_hero_title: "Return Policy",
  returns_hero_subtitle: "How orders, cancellations, and refunds work through our printing partner",
  returns_content: `<p>Our games are printed and fulfilled on demand by <strong>TheGameCrafter</strong>, so their return policy applies to every order placed through our shop.</p><h2>Orders Are Made to Order</h2><p>TheGameCrafter creates all games on demand through an automated process. Because of this, orders cannot be canceled once the order status reaches <strong>"in production."</strong> At that point the game is being produced, and TheGameCrafter cannot make changes to the order or issue refunds. All sales are final upon purchase.</p><h2>Canceling Before Production</h2><p>It is possible to cancel an order before it enters production:</p><ol><li>Log into your TheGameCrafter account.</li><li>Click "Shopping" near the top left of the screen, then select "Receipts" from the drop-down list.</li><li>Click on your order number.</li><li>If your order has not yet entered production, you'll see the option to cancel on the right-hand side of the screen.</li></ol><h2>Refunds</h2><p>When your order is canceled, you'll receive an in-shop credit for the total. You may request a refund via your original payment method by contacting TheGameCrafter through the green "Get Support" button at the bottom right of their site, or by email at <a href="mailto:info@thegamecrafter.com">info@thegamecrafter.com</a>.</p><p><strong>Note:</strong> if you have downloaded files from the game that are available only after purchase, you will not be able to cancel or refund your order, as you have already received part of the game.</p><hr><h2>Questions?</h2><p>You can read the full policy on <a href="https://help.thegamecrafter.com/article/136-return-policy" target="_blank" rel="noopener noreferrer">TheGameCrafter's Return Policy page</a>. If you have a question about your order, feel free to <a href="/contact">reach out to us</a> as well.</p>`,
  privacy_hero_title: "Privacy Policy",
  privacy_hero_subtitle: "How we collect, use, and protect your information",
  privacy_content: `<p>Rogue Skull Games LLC ("Rogue Skull Games," "we," "us") respects your privacy. This policy explains what information we collect through rogueskullgames.com, how we use it, and the choices you have. By using our site you agree to the practices described here.</p><h2>Information We Collect</h2><ul><li><strong>Information you give us.</strong> Your name and email address when you join our Captain's Log newsletter, the email address you enter at checkout, and anything you include when you email us.</li><li><strong>Order information.</strong> The games and quantities in your bag when you begin checkout.</li><li><strong>Automatically collected information.</strong> Standard usage data such as pages viewed, referring site, browser type, device type, and approximate location, gathered through cookies and analytics tools.</li></ul><h2>How We Use Your Information</h2><ul><li>To hand your bag off to our printing partner and help you complete your order.</li><li>To send you news about new games, releases, and studio updates when you opt in.</li><li>To answer your questions, rules inquiries, and other messages.</li><li>To understand how our site is used so we can improve it.</li></ul><h2>Payments and Fulfillment</h2><p>We do not process payments or ship products ourselves. Our games are printed and fulfilled on demand by <strong>TheGameCrafter</strong>. When you check out, you are taken to TheGameCrafter's site, where your payment and shipping details are collected and handled under <a href="https://www.thegamecrafter.com/privacy" target="_blank" rel="noopener noreferrer">their privacy policy</a>. Rogue Skull Games never sees or stores your payment card information.</p><h2>Service Providers We Share Data With</h2><p>We share only what is needed with the providers that help us run the studio:</p><ul><li><strong>TheGameCrafter</strong> — printing, payment, and fulfillment of orders.</li><li><strong>Mailchimp</strong> — newsletter delivery and list management.</li><li><strong>Google Analytics / Google Tag Manager</strong> — website usage measurement.</li></ul><p>We do not sell your personal information, and we do not rent or trade our mailing list.</p><h2>Cookies and Tracking</h2><p>Our site uses cookies and similar technologies to keep your bag contents, remember preferences, and measure traffic. You can block or delete cookies in your browser settings, though some parts of the site may not work as expected if you do.</p><h2>Email Choices</h2><p>Every newsletter we send includes an unsubscribe link, and you can opt out at any time. You may also email us to be removed from our list. We may still contact you about an order you placed.</p><h2>Data Retention</h2><p>We keep newsletter subscriptions until you unsubscribe, and we keep checkout contact details only as long as they are useful for supporting orders and studio communications.</p><h2>Children's Privacy</h2><p>Our games are made for players of many ages, but this website is intended for adults. We do not knowingly collect personal information from children under 13. If you believe a child has provided us information, contact us and we will delete it.</p><h2>Your Rights</h2><p>Depending on where you live, you may have the right to request a copy of the personal information we hold about you, ask us to correct it, or ask us to delete it. Email us and we will respond as required by applicable law.</p><h2>Security</h2><p>We use reasonable safeguards to protect the information we hold, but no method of transmission or storage over the internet is completely secure.</p><h2>Changes to This Policy</h2><p>We may update this policy as our studio and site evolve. The most current version will always be posted on this page.</p><hr><h2>Contact Us</h2><p>Questions about this policy? Email <a href="mailto:rogueskullgames@gmail.com">rogueskullgames@gmail.com</a> or use our <a href="/contact">contact page</a>.</p>`,
  terms_hero_title: "Terms of Use",
  terms_hero_subtitle: "The rules of the table for using our site and buying our games",
  terms_content: `<p>Welcome to rogueskullgames.com, operated by Rogue Skull Games LLC, a Kansas limited liability company. By browsing this site, joining our newsletter, or placing an order through our shop, you agree to these Terms of Use. If you do not agree, please do not use the site.</p><h2>Using the Site</h2><p>You agree to use this site only for lawful purposes and not to interfere with its operation, attempt to gain unauthorized access to any part of it, scrape or copy it for commercial use, or misuse any forms or contact features.</p><h2>Orders and Fulfillment</h2><p>Our games are printed and fulfilled on demand by <strong>TheGameCrafter</strong>. When you check out, your order is placed with and processed by TheGameCrafter, and their terms, pricing, shipping timelines, and policies apply to that transaction. Prices shown on our site may change without notice, and product availability is not guaranteed.</p><h2>Returns and Cancellations</h2><p>Because every game is made to order, cancellations and refunds follow our printing partner's rules. Please see our <a href="/return-policy">Return Policy</a> for details.</p><h2>Intellectual Property</h2><p>All game titles, rules text, artwork, logos, characters, photography, and site content are owned by Rogue Skull Games LLC or used with permission, and are protected by copyright and trademark law. You may not reproduce, distribute, sell, or create derivative works from our content without written permission.</p><h2>Fan Content and Community Use</h2><p>We love seeing our games at your table. You may share photos, videos, reviews, playthroughs, and non-commercial fan content featuring our games, provided you credit Rogue Skull Games and do not imply official sponsorship or endorsement. Selling components, print-and-play copies, or merchandise using our intellectual property is not permitted.</p><h2>Unsolicited Submissions</h2><p>Please do not send us game designs, prototypes, or other unsolicited creative submissions. Any material you send us anyway is considered non-confidential, and we are under no obligation to review, return, or compensate for it.</p><h2>Links to Other Sites</h2><p>Our site links to third-party sites, including our printing partner and social platforms. We are not responsible for the content, terms, or privacy practices of those sites.</p><h2>Disclaimer</h2><p>This site and its content are provided "as is." While we work to keep information such as player counts, ages, prices, and release timing accurate, we do not warrant that the site will be error free or uninterrupted, or that all content is complete and current.</p><h2>Limitation of Liability</h2><p>To the fullest extent permitted by law, Rogue Skull Games LLC is not liable for any indirect, incidental, or consequential damages arising from your use of this site.</p><h2>Governing Law</h2><p>These terms are governed by the laws of the United States and the State of Kansas, where Rogue Skull Games LLC is organized, without regard to conflict of law rules.</p><h2>Changes to These Terms</h2><p>We may update these terms from time to time. Continued use of the site after changes are posted means you accept the updated terms.</p><hr><h2>Contact Us</h2><p>Questions about these terms? Email <a href="mailto:rogueskullgames@gmail.com">rogueskullgames@gmail.com</a> or visit our <a href="/contact">contact page</a>.</p>`,
};


const SiteSettingsContext = createContext<SiteSettingsContextType | undefined>(undefined);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    const { data, error } = await supabase
      .from("site_settings")
      .select("setting_key, setting_value");

    if (!error && data) {
      const newSettings: Partial<SiteSettings> = {};
      data.forEach((row) => {
        if (row.setting_key in defaultSettings) {
          newSettings[row.setting_key as keyof SiteSettings] = row.setting_value || "";
        }
      });
      setSettings((prev) => ({ ...prev, ...newSettings }));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const refreshSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  return (
    <SiteSettingsContext.Provider value={{ settings, isLoading, refreshSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error("useSiteSettings must be used within a SiteSettingsProvider");
  }
  return context;
}
